import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../services/auth.service";
import OrderInterface from "../interfaces/OrderInterface";
import OrderService from "../services/order.service";
import {
  Alert,
  AlertTitle,
  Box,
  Breadcrumbs,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import statusProps from "../utils/StatusUtils";
import moment from "moment";
import vehicleCategoriesProps from "../utils/VehicleCategoriesUtils";
import AppContext from "../contexts/AppContext";
import functionsUser from "../services/user.service";
import useInterval from "../utils/useInterval";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const Orders = () => {
  const appContext = useContext(AppContext);
  const history = useNavigate();
  const [orders, setOrders] = useState<OrderInterface[]>();
  const [loadingOrders, setLoadingOrders] = useState<boolean>(true);
  const [isLoadingPrepaidBalance, setIsLoadingPrepaidBalance] =
    useState<boolean>(true);
  const [prepaidBalance, setPrepaidBalance] = useState<number>(0);

  const fetchOrders = useCallback(() => {
    // setLoadingOrders(true);
    if (appContext?.userID) {
      OrderService.getUserHistoricOrders(appContext?.userID ?? "", "ANY")
        .then((response) => {
          setOrders(
            response.data.filter((order: any) => order.status !== "PRE_ORDER")
          );
          setLoadingOrders(false);
        })
        .catch((error) => {
          if (JSON.stringify(error).includes("401")) {
            AuthService.Logout();
          }
          setLoadingOrders(false);
        });
    }
  }, [appContext?.userID]);

  const fetchPrepaidBalance = useCallback(() => {
    // setIsLoadingPrepaidBalance(true);
    functionsUser
      .getUserPrepaidBalance()
      .then((response) => {
        setIsLoadingPrepaidBalance(false);
        setPrepaidBalance(response.data.prepaidBalance);
      })
      .catch((error) => {
        if (JSON.stringify(error).includes("401")) {
          AuthService.Logout();
        }
        setIsLoadingPrepaidBalance(false);
      });
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    fetchPrepaidBalance();
  }, [fetchPrepaidBalance]);

  useInterval(() => {
    fetchOrders();
  }, 5000);

  useInterval(() => {
    fetchPrepaidBalance();
  }, 5000);

  return (
    <>
      <Box m={2}>
        <Breadcrumbs aria-label="breadcrumb">
          <Typography color="text.primary" aria-current="page">
            Página Inicial
          </Typography>
        </Breadcrumbs>
      </Box>
      <Container>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12} lg={6}>
            <Card
              style={{
                backgroundColor: "#FFF",
                borderRadius: 16,
                padding: 24,
                boxShadow:
                  "rgb(145 158 171 / 24%) 0px 0px 2px 0px, rgb(145 158 171 / 24%) 0px 16px 32px -4px",
              }}
            >
              {isLoadingPrepaidBalance !== undefined &&
              isLoadingPrepaidBalance === true ? (
                <>
                  <Box style={{ textAlign: "center" }}>
                    <CircularProgress size={96} style={{}} />
                  </Box>
                </>
              ) : (
                <>
                  <Typography
                    component={"h4"}
                    variant={"body1"}
                    style={{
                      fontWeight: "600",
                    }}
                  >
                    Saldo pré-pago
                  </Typography>
                  <Typography
                    component={"h3"}
                    variant={"h3"}
                    style={{
                      fontWeight: "600",
                    }}
                  >
                    {Number(prepaidBalance).toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </Typography>
                  <Link
                    // component={"span"}
                    // variant={"caption"}
                    // style={{
                    // 	fontWeight: '400',
                    // 	color: '#637381'
                    // }}
                    onClick={() => {
                      history("/payment");
                    }}
                    href={"/payment"}
                  >
                    Adicionar saldo
                  </Link>
                </>
              )}
            </Card>
          </Grid>
          <Grid item xs={6} md={6} lg={6}></Grid>
        </Grid>
        <Box>
          <Typography variant="h4">Pedidos</Typography>
          <Typography variant="subtitle1">
            Gerência dos pedidos. Ordenado pela data de criação do pedido (mais
            recente primeiro). Exibindo os 10 mais recentes.
          </Typography>
        </Box>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          gap={4}
          mt={1}
          mb={2}
        >
          <Button
            href={"v2/order/create"}
            variant={"contained"}
            color={"primary"}
          >
            Novo pedido
          </Button>
        </Box>
        {!loadingOrders ? (
          <>
            <div>
              {orders !== undefined && orders !== null && orders.length > 0 ? (
                <>
                  <Box
                    sx={{ display: { xs: "block", md: "none", lg: "none" } }}
                    mb={10}
                  >
                    <List dense>
                      {orders.map((order) => (
                        <ListItem
                          key={order.id}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              href={`/order/${order.id}`}
                            >
                              <ArrowForwardIcon />
                            </IconButton>
                          }
                        >
                          <ListItemText
                            primary={
                              moment(order.startTime).format("DD/MM - HH:mm") +
                              " | " +
                              order.price.toLocaleString("pt-BR", {
                                style: "currency",
                                currency: "BRL",
                              })
                            }
                            secondary={
                              <Chip
                                variant={"outlined"}
                                size={"small"}
                                label={
                                  statusProps.filter(function (status) {
                                    return status.status === order.status;
                                  })[0]?.text
                                }
                                color={"primary"}
                                style={{
                                  backgroundColor: statusProps.filter(function (
                                    status
                                  ) {
                                    return status.status === order.status;
                                  })[0]?.backgroundColor,
                                  color: statusProps.filter(function (status) {
                                    return status.status === order.status;
                                  })[0]?.textColor,
                                  borderWidth: 0,
                                }}
                              />
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                  <Box
                    sx={{ display: { xs: "none", md: "block", lg: "block" } }}
                    mb={10}
                  >
                    <TableContainer
                      component={Paper}
                      style={{ marginBottom: 100 }}
                    >
                      <Box>
                        <Table size={"medium"} aria-label="a dense table">
                          <TableHead
                            style={{
                              backgroundColor: "rgba(243,244,246,1)",
                              color: "rgba(107,114,128,1)",
                            }}
                          >
                            <TableRow>
                              <TableCell align="left">DATA</TableCell>
                              <TableCell align="left">STATUS</TableCell>
                              <TableCell align="left">CATEGORIA</TableCell>
                              <TableCell align="left">PREÇO</TableCell>
                              <TableCell align="left">ENTREGADOR</TableCell>
                              <TableCell align="left"></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {orders.map((order) => (
                              <TableRow hover key={order.id}>
                                <TableCell>
                                  <Typography variant={"body2"}>
                                    {moment(order.startTime).format(
                                      "DD/MM - HH:mm"
                                    )}
                                  </Typography>
                                  <Typography variant={"caption"}>
                                    {moment(order.startTime).fromNow()}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    variant={"outlined"}
                                    size={"small"}
                                    label={
                                      statusProps.filter(function (status) {
                                        return status.status === order.status;
                                      })[0]?.text
                                    }
                                    color={"primary"}
                                    style={{
                                      backgroundColor: statusProps.filter(
                                        function (status) {
                                          return status.status === order.status;
                                        }
                                      )[0]?.backgroundColor,
                                      color: statusProps.filter(function (
                                        status
                                      ) {
                                        return status.status === order.status;
                                      })[0]?.textColor,
                                      borderWidth: 0,
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  {
                                    vehicleCategoriesProps.find((category) => {
                                      return (
                                        category.number ===
                                        order.vehicleCategory
                                      );
                                    })?.label
                                  }
                                </TableCell>
                                <TableCell>
                                  {order.price.toLocaleString("pt-BR", {
                                    style: "currency",
                                    currency: "BRL",
                                  })}
                                </TableCell>
                                <TableCell>
                                  {!!order.deliveryMan ? (
                                    <>
                                      <Typography variant={"body2"}>
                                        {order.deliveryMan?.name}
                                      </Typography>
                                      <Typography variant={"caption"}>
                                        {order.deliveryMan?.phone}
                                      </Typography>
                                    </>
                                  ) : (
                                    "-/-"
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Button
                                    size={"small"}
                                    href={`/order/${order.id}`}
                                  >
                                    Visualizar
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </TableContainer>
                  </Box>
                </>
              ) : (
                <>
                  <Alert severity={"info"}>
                    <AlertTitle>Aviso!</AlertTitle>
                    Nenhum pedido localizado para este filtro e página.
                  </Alert>
                </>
              )}
            </div>
          </>
        ) : (
          <>
            <CircularProgress />
          </>
        )}
      </Container>
    </>
  );
};

export default Orders;
